import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Code2, Eye, EyeOff, LayoutList } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { ConfigProblems } from "../bots/ProjectConfigDialogs";
import { settingsLink } from "../bots/links";
import {
  botsProblemsOf,
  type BotsProblem,
} from "../bots/settings/botsValidation";
import { Button } from "../../components/Button/Button";
import { ChipInput } from "../../components/ChipInput/ChipInput";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import { MetaSchemaEditor } from "../../components/MetaSchemaEditor/MetaSchemaEditor";
import { NumberInputWithUnit } from "../../components/NumberInputWithUnit/NumberInputWithUnit";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import {
  SchemaEditor,
  type SchemaField,
} from "../../components/SchemaEditor/SchemaEditor";
import { Select } from "../../components/Select/Select";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { Switch } from "../../components/Switch/Switch";
import { Textarea } from "../../components/Textarea/Textarea";
import { useToast } from "../../components/Toast/toastContext";
import { Card } from "../../components/Card/Card";
import { ApiError, describeError } from "../../lib/errors";
import { schemaKeyOptionsWithUnset } from "../../lib/schemaFields";
import {
  getReferralStats,
  getSigningStats,
  patchProject,
  type ReferralStats as ReferralStatsData,
  type SigningStats,
} from "./api";
import { projectQueryKey, useProject, writeSavedProject } from "./useProject";
import { useConfigOutline } from "../../app/configOutlineContext";
import { SECTION_META, SECTION_ORDER } from "./config/types";
import { FieldRow } from "./config/FieldRow";
import { SectionCard } from "./config/SectionCard";
import { StickySaveBar } from "./config/StickySaveBar";
import { DEFAULT_CONFIG, normalizeConfig } from "./config/defaults";
import { ProfanityWordsField } from "./config/ProfanityWordsField";
import { HelpTypesEditor } from "./config/HelpTypesEditor";
import { GiftChannelsField } from "./config/GiftChannelsField";
import { GiftTypesEditor } from "./config/GiftTypesEditor";
import { InviteTriggerField } from "./config/InviteTriggerField";
import { InviteeRewardEditor } from "./config/InviteeRewardEditor";
import { InviterRewardsEditor } from "./config/InviterRewardsEditor";
import { ReferralStats } from "./config/ReferralStats";
import { StatKeySelect } from "./config/StatKeySelect";
import { RequestSigningStats } from "./config/RequestSigningStats";
import {
  SIGNED_SHARE_THRESHOLD,
  formatCount,
  formatShare,
  signingRollout,
} from "./config/signingRollout";
import { TTL_MAX, TTL_MIN, helpTypesError } from "./config/helpTypes";
import { giftTypesError } from "./config/giftTypes";
import {
  TRIGGER_VALUE_MAX,
  inviterRewardsError,
  rewardFieldError,
} from "./config/invites";
import {
  countChanges,
  describeJsonError,
  humanizeDuration,
  isEqual,
  isSectionDirty,
  rangeError,
} from "./config/utils";
import type {
  ChatConfig,
  CloudSaveConfig,
  FacebookAuthConfig,
  GiftConfig,
  GuildConfig,
  GuildRecommendationsConfig,
  HelpRequestConfig,
  InvitesConfig,
  ProfileConfig,
  ProjectConfig,
  RateLimitsConfig,
  GuildAssetsConfig,
  RequestSigningConfig,
} from "./config/types";
import styles from "./ProjectConfigPage.module.css";

type Mode = "forms" | "json";

const AVATAR_REGEX = /^[a-z0-9_]+$/i;
const META_KEY_REGEX = /^[a-z_][a-z0-9_]*$/;

/** The storage-policy line sits outside the Input, so it needs a stable id to
 *  be referenced from the app-secret field's aria-describedby. */
const SECRET_HELPER_ID = "auth-facebook-secret-helper";

/**
 * Helper line under the meta-schema editor. Spells out the empty case,
 * because it reads the opposite way to its neighbours: an empty
 * `stats_schema` accepts any object and an empty `allowed_avatars` accepts
 * any string, but an empty `meta_schema` accepts nothing at all.
 */
function metaSchemaHelper(schema: ProfileConfig["meta_schema"]): string {
  if (schema.length === 0) {
    return "Empty schema — players.meta stays {} and PATCH /v1/me/meta rejects every key. Unlike stats_schema, empty here does not mean “accept anything”.";
  }
  const noun = schema.length === 1 ? "field" : "fields";
  return `${schema.length} ${noun} · players write them via PATCH /v1/me/meta`;
}

/**
 * What the selected mode DOES, said in the row's own hint. It changes with the
 * choice because the three modes are not three settings of one behaviour —
 * `log` and `enforce` run exactly the same check and differ only in what they
 * do with the verdict, and that is the sentence an operator is deciding on.
 */
const SIGNING_MODES = ["off", "log", "enforce"] as const;

const SIGNING_MODE_HINT: Record<string, ReactNode> = {
  off: (
    <>
      <code>off</code> skips the check entirely — no counters, no refusals.
    </>
  ),
  log: (
    <>
      <code>log</code> runs the full check and refuses nothing — it only fills
      the counters above.
    </>
  ),
  enforce: (
    <>
      <code>enforce</code> refuses every request that fails the check.
    </>
  ),
};

/**
 * The clock-skew helper: the default line, or what the blob was overruled on.
 *
 * The two verdicts are decided by the caller, which is the only place that can
 * see both the flag and the value the box is showing.
 */
function signingSkewHelper(
  raw: unknown,
  state: { clamped: boolean; invalid: boolean; inEffect: number | undefined },
): { text: ReactNode; warn: boolean } {
  const inEffect = state.inEffect ?? 300;
  if (state.clamped) {
    return {
      text: (
        <>
          Above the {inEffect} s ceiling — <code>{inEffect}</code> is in effect.
        </>
      ),
      warn: true,
    };
  }
  if (state.invalid) {
    // JSON.stringify rather than String: it is the blob's own spelling that is
    // being quoted, and "5m" with its quotes is what the operator will find
    // when they open the Raw JSON view to fix it.
    return {
      text: (
        <>
          Config says <code>{JSON.stringify(raw)}</code> — not a whole number,
          so the default <code>{inEffect}</code> is in effect.
        </>
      ),
      warn: true,
    };
  }
  return { text: "Default 300. Capped at 900.", warn: false };
}

/**
 * The Request signing counters, under the project's own prefix like everything
 * else a project owns — so whatever drops the project drops these with it.
 */
function signingStatsQueryKey(projectId: string) {
  return [...projectQueryKey(projectId), "signing-stats"] as const;
}

/** The Invites counters, under the same prefix and for the same reason. */
function referralStatsQueryKey(projectId: string) {
  return [...projectQueryKey(projectId), "referral-stats"] as const;
}

export function ProjectConfigPage() {
  const { id = "" } = useParams<{ id: string }>();
  // Another project is another editor: remounting drops the draft the previous
  // one held, which is what the bootstrap effect used to do by hand.
  return <ProjectConfigEditor key={id} projectId={id} />;
}

function ProjectConfigEditor({ projectId: id }: { projectId: string }) {
  const qc = useQueryClient();
  const { push } = useToast();
  const { setOutline } = useConfigOutline();
  const { hash } = useLocation();

  const { data, refetch, isLoading } = useProject(id);

  // The Request signing section's numbers, and what the server's parser makes
  // of that section of the blob.
  //
  // Read once when the page opens and never refetched: the window is seven days
  // wide and does not move while somebody is looking at it (§8.3). A failure is
  // not handled here — the section has a shape for "the store did not answer",
  // and nothing else on this page depends on the answer.
  const signingQuery = useQuery({
    queryKey: signingStatsQueryKey(id),
    queryFn: () => getSigningStats(id),
    enabled: !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Server snapshot — read straight off the query, so the last successful PATCH
  // (which writes the cache) and any refetch are already in it, with no state to
  // keep in step. What the user is editing is a draft laid over it: `null` means
  // "nothing typed yet, show the server's answer", and dropping the draft is how
  // both Discard and a completed save get back to it.
  //
  // Normalising HERE, on the read, is what keeps the shared cache entry to one
  // form: the raw project as the server sent it. Normalising into the cache
  // instead would put a second form under the same key — the very split this
  // page's own key used to be part of.
  const serverConfig = useMemo(
    () => (data ? normalizeConfig(data.config) : DEFAULT_CONFIG),
    [data],
  );
  // The Invites section's counters, read once and never refetched like the
  // signing ones — and asked for only while the SAVED config has the mechanic
  // on. Numbers about a feature nobody enabled are numbers about nothing, and
  // the working draft must not decide it: ticking the switch would otherwise
  // fetch a funnel that does not exist yet.
  const invitesEnabled = serverConfig.invites.enabled;
  const referralQuery = useQuery({
    queryKey: referralStatsQueryKey(id),
    queryFn: () => getReferralStats(id),
    enabled: !!id && invitesEnabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [draft, setDraft] = useState<ProjectConfig | null>(null);
  const workingConfig = draft ?? serverConfig;

  function setWorkingConfig(
    next: ProjectConfig | ((prev: ProjectConfig) => ProjectConfig),
  ) {
    setDraft((prev) =>
      typeof next === "function" ? next(prev ?? serverConfig) : next,
    );
  }

  const [mode, setMode] = useState<Mode>("forms");
  // Same shape for the Raw JSON box: the typed text while there is any, the
  // pretty-printed working config otherwise — which is what kept the two views
  // in step before, one render later, through an effect.
  const [rawDraft, setRawDraft] = useState<string | null>(null);
  const rawJson = rawDraft ?? JSON.stringify(workingConfig, null, 2);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveProblems, setSaveProblems] = useState<BotsProblem[]>([]);
  /**
   * The fields a refusal NAMED, when it named any.
   *
   * `bots` has no card on this page any more, but the backend still parses the
   * WHOLE blob on PATCH and this page still sends `bots` in it. So a project
   * whose bots section is invalid — written in the JSON view above, restored
   * from a snapshot, or saved before the rule existed — cannot save a change
   * to chat, guilds or profile either, and `invalid_bots_config` says «see the
   * list below». Without the list that sentence points at nothing and the
   * operator has no way off this page.
   */
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [enforceOpen, setEnforceOpen] = useState(false);
  // Scroll-spy: which section is currently in view (drives the sidebar sub-nav).
  const [activeSection, setActiveSection] = useState<string>(SECTION_ORDER[0]);

  const dirty = useMemo(() => {
    if (!isEqual(serverConfig, workingConfig)) return true;
    // Raw JSON edits show up in `rawJson` only when parse fails — in that
    // case workingConfig isn't updated but the user clearly has unsaved
    // text. Mockup L824: bar visible with "Invalid JSON…" so user can
    // still see Discard.
    if (mode === "json" && jsonError != null) {
      return rawJson !== JSON.stringify(serverConfig, null, 2);
    }
    return false;
  }, [serverConfig, workingConfig, mode, jsonError, rawJson]);

  const { changes, sections } = useMemo(
    () => countChanges(serverConfig, workingConfig),
    [serverConfig, workingConfig],
  );

  // Field-level validation. Save disabled when any field is invalid.
  const fieldErrors = useMemo(
    () => validateConfig(workingConfig),
    [workingConfig],
  );
  const hasFieldError = useMemo(
    () => Object.values(fieldErrors).some((v) => v != null),
    [fieldErrors],
  );
  // beforeunload guard — only attached while dirty so unaffected tabs are
  // free to navigate without confirm dialogs. react-router v6 useBlocker
  // would be nicer but isn't available on this version yet.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Scroll-spy — observe the section cards and track which one is in view.
  // Drives the active state of the sidebar sub-nav (mockup L355 `.sub-item.active`).
  useEffect(() => {
    if (mode !== "forms" || isLoading) return;
    if (typeof IntersectionObserver === "undefined") return;
    const els = SECTION_ORDER.map((key) =>
      document.getElementById(key),
    ).filter((el): el is HTMLElement => el != null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -55% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [mode, isLoading]);

  /**
   * Arriving from a link that names a CARD — the bots dialogs send operators
   * here to declare a schema, `#profile` from the card-rules editor and
   * `#guild` from the guild-template one.
   *
   * The anchors have always been on the cards; nothing ever drove to them. A
   * client-side navigation does not scroll on its own, react-router only does
   * it through a `<ScrollRestoration>` this app never mounts, and the page is
   * long enough that landing at its top and hunting for «Guild defaults» is
   * the same dead end the link was written to replace.
   *
   * Once per hash, and never while the page is still empty: the effect re-runs
   * on every render of a loaded page, and a card that yanked itself back into
   * view under somebody's cursor would be a bug rather than a courtesy. Same
   * shape as the Bots settings tab, which solved this for its own links.
   */
  const scrolledTo = useRef("");
  useEffect(() => {
    if (mode !== "forms" || isLoading) return;
    const target = hash.replace(/^#/, "");
    if (!target || scrolledTo.current === hash) return;
    const el = document.getElementById(target);
    if (!el) return;
    scrolledTo.current = hash;
    // Guarded the way Menu and AppSelector guard theirs: jsdom has no
    // scrollIntoView, and a test that lands here should read the card rather
    // than throw over how it was scrolled to.
    el.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [hash, mode, isLoading]);

  // Publish the section outline to the AppShell sidebar sub-nav. Cleared on
  // unmount / when leaving forms mode so the sub-nav only shows on this page.
  useEffect(() => {
    if (mode !== "forms" || isLoading) {
      setOutline({ sections: [], activeId: "" });
      return;
    }
    setOutline({
      sections: SECTION_ORDER.map((key) => ({
        id: key,
        label: SECTION_META[key].title,
        dirty: isSectionDirty(serverConfig, workingConfig, key),
      })),
      activeId: activeSection,
    });
  }, [
    mode,
    isLoading,
    serverConfig,
    workingConfig,
    activeSection,
    setOutline,
  ]);

  useEffect(
    () => () => setOutline({ sections: [], activeId: "" }),
    [setOutline],
  );

  function setChat<K extends keyof ChatConfig>(key: K, value: ChatConfig[K]) {
    setWorkingConfig((prev) => ({ ...prev, chat: { ...prev.chat, [key]: value } }));
  }
  function setGuild<K extends keyof GuildConfig>(
    key: K,
    value: GuildConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      guild: { ...prev.guild, [key]: value },
    }));
  }
  function setHelp<K extends keyof HelpRequestConfig>(
    key: K,
    value: HelpRequestConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      help_request: { ...prev.help_request, [key]: value },
    }));
  }
  function setGift<K extends keyof GiftConfig>(key: K, value: GiftConfig[K]) {
    setWorkingConfig((prev) => ({
      ...prev,
      gift: { ...prev.gift, [key]: value },
    }));
  }
  function setInvites<K extends keyof InvitesConfig>(
    key: K,
    value: InvitesConfig[K],
  ) {
    setWorkingConfig((prev) => {
      const invites = { ...prev.invites, [key]: value };
      // The invitee's reward is the section's only optional key, and "pays
      // nobody" is an ABSENT key rather than an `undefined` one — the shape
      // the normalizer leaves and the server reads.
      if (value === undefined) delete invites[key];
      return { ...prev, invites };
    });
  }
  function setRate<K extends keyof RateLimitsConfig>(
    key: K,
    value: RateLimitsConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      rate_limits: { ...prev.rate_limits, [key]: value },
    }));
  }
  function setFacebookAuth<K extends keyof FacebookAuthConfig>(
    key: K,
    value: FacebookAuthConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      auth: {
        ...prev.auth,
        facebook: { ...prev.auth.facebook, [key]: value },
      },
    }));
  }
  function setCloudSave<K extends keyof CloudSaveConfig>(
    key: K,
    value: CloudSaveConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      cloud_save: { ...prev.cloud_save, [key]: value },
    }));
  }
  function setProfile<K extends keyof ProfileConfig>(
    key: K,
    value: ProfileConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      profile: { ...prev.profile, [key]: value },
    }));
  }
  function setGuildAssets<K extends keyof GuildAssetsConfig>(
    key: K,
    value: GuildAssetsConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      guild_assets: { ...prev.guild_assets, [key]: value },
    }));
  }
  function setSigning<K extends keyof RequestSigningConfig>(
    key: K,
    value: RequestSigningConfig[K],
  ) {
    setWorkingConfig((prev) => ({
      ...prev,
      request_signing: { ...prev.request_signing, [key]: value },
    }));
  }

  /** Leaving one view for the other hands the box back its pre-rendered text. */
  function switchMode(next: Mode) {
    setMode(next);
    setRawDraft(null);
    setJsonError(null);
  }

  function handleRawChange(next: string) {
    setRawDraft(next);
    if (next.trim() === "") {
      setJsonError("Required");
      return;
    }
    try {
      const parsed = JSON.parse(next);
      // `normalizeConfig` builds this blob out of its seven known sections, so
      // anything else in the pasted text is dropped on the way in — silently,
      // by the browser, before the server ever gets a chance to refuse it. For
      // `bots` that is exactly the loss this page must not commit: the settings
      // moved to a table of their own, and an operator pasting them here would
      // press Save, be told «Config saved.», and have saved nothing.
      if (parsed && typeof parsed === "object" && "bots" in parsed) {
        setJsonError(
          "`bots` is not part of this config any more — the bot settings are " +
            "their own document, edited on the Bots page.",
        );
        return;
      }
      setJsonError(null);
      // Normalize so partial JSON doesn't lose required keys.
      setWorkingConfig(normalizeConfig(parsed));
    } catch (err) {
      setJsonError(describeJsonError(next, err));
    }
  }

  async function doSave() {
    if (!data) return;
    if (mode === "json" && jsonError) return;
    if (hasFieldError) return;
    setSaving(true);
    setSaveError(null);
    setSaveProblems([]);
    try {
      const rawEtag = (data.etag ?? "")
        .replace(/^W\//, "")
        .replace(/^"|"$/g, "");
      const r = await patchProject(
        id,
        { config: workingConfig },
        rawEtag,
      );
      setDraft(null);
      setRawDraft(null);
      // The counters' answer carries the server's reading of the blob that has
      // just been replaced, so it goes BEFORE the new snapshot is published —
      // and it is thrown away rather than marked stale. An invalidation leaves
      // the old answer in place until the refetch lands, and for those two
      // round trips the section would be describing the previous version of the
      // config beside the new one. Dropping it costs one strip of numbers and
      // buys an empty box in place of a wrong sentence.
      qc.removeQueries({ queryKey: signingStatsQueryKey(id) });
      // The saved project is the new server snapshot, so it goes into the entry
      // every screen of this project reads — through the one writer, which owns
      // what that means (see writeSavedProject).
      writeSavedProject(qc, id, r.data, workingConfig);
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
      push({ variant: "success", message: "Config saved." });
    } catch (err) {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        // v1: a generic "reload" modal. Mockup L803 specifies a diff modal
        // surfacing author + timestamp — deferred until backend exposes
        // last-editor metadata.
        setConflictOpen(true);
      } else if (err instanceof ApiError) {
        setSaveError(describeError(err.code));
        setSaveProblems(botsProblemsOf(err));
      } else {
        setSaveError(describeError("internal"));
      }
    } finally {
      setSaving(false);
    }
  }

  function confirmDiscard() {
    setDraft(null);
    setRawDraft(null);
    setJsonError(null);
    setSaveError(null);
    setSaveProblems([]);
    setDiscardOpen(false);
  }

  async function handleConflictReload() {
    // The modal promises the current edits are discarded — they are dropped
    // here rather than left for the refetch to overwrite.
    setConflictOpen(false);
    setDraft(null);
    setRawDraft(null);
    setJsonError(null);
    setSaveError(null);
    setSaveProblems([]);
    // Somebody else's blob is about to arrive, and the counters' answer was
    // read against ours. Same reasoning as in doSave: drop it, do not merely
    // flag it.
    qc.removeQueries({ queryKey: signingStatsQueryKey(id) });
    await refetch();
  }

  const breadcrumb = [
    { label: "All projects", to: "/projects" },
    { label: data?.name ?? "Project" },
  ];

  const actions = (
    <SegmentedToggle<Mode>
      aria-label="Edit mode"
      value={mode}
      onChange={switchMode}
      options={[
        { value: "forms", label: "Forms", icon: <LayoutList size={12} /> },
        { value: "json", label: "Raw JSON", icon: <Code2 size={12} /> },
      ]}
    />
  );

  const header = (
    <PageHeader
      breadcrumb={breadcrumb}
      title="Configuration"
      actions={actions}
    />
  );

  if (isLoading || !data) {
    return (
      <>
        {header}
        <div className={styles.page}>Loading…</div>
      </>
    );
  }

  const saveDisabled =
    !dirty || hasFieldError || (mode === "json" && jsonError != null);

  /**
   * Turning enforce ON — not sitting in it. The dialog below asks about the
   * change, so a project already enforcing says nothing when its clock skew is
   * edited, and neither does flipping the toggle back to where it started.
   */
  const enteringEnforce =
    workingConfig.request_signing.mode === "enforce" &&
    serverConfig.request_signing.mode !== "enforce";
  const rollout = signingRollout(signingQuery.data);
  /**
   * The second of the two guards, and the last one: the banner speaks where the
   * choice is made, this speaks where it is applied — Save lives in a fixed bar
   * ten sections below the toggle, and one can reach it without ever going back
   * up. Above the threshold it does not open at all; nothing here can REFUSE a
   * save, because the studio's own number is the studio's to pick (§11.3).
   */
  const enforceNeedsConfirm =
    enteringEnforce &&
    (rollout == null || rollout.signedShare < SIGNED_SHARE_THRESHOLD);

  function requestSave() {
    if (enforceNeedsConfirm) {
      setEnforceOpen(true);
      return;
    }
    void doSave();
  }

  /**
   * What the save bar says when the problem has no field of its own to sit
   * under. The row editors paint their own cells, but a list-level rule — a
   * duplicate type, a quota the server would not read — is about the LIST, and
   * a greyed-out button on its own names nothing.
   *
   * Two lists can raise one now, and the bar has a single line to say it in, so
   * the first one that is actually broken speaks. That is not a ranking of
   * importance: `hasFieldError` already blocks Save on either, the operator
   * fixes what they are told about, and the other takes its place on the next
   * render. Adding a third list means adding it here — which is the price of
   * one line, and cheaper than a bar that grows a second row of text.
   */
  const listError =
    fieldErrors["help_request.types"] ??
    fieldErrors["gift.types"] ??
    // The invitee's reward is not a list, but it is the same problem: what is
    // in the box is not a reward yet, and the box paints itself red without
    // ever naming which reward on the page the bar is refusing to save.
    fieldErrors["invites.invitee_reward"] ??
    fieldErrors["invites.inviter_rewards"];

  return (
    <>
      {header}
      <div className={styles.page}>
        <div className={styles.grid}>
          {saveError && (
            // The fields it names may well be on ANOTHER page. The bot
            // settings left this blob, but the dependency did not: they are
            // parsed against this project's card schema, so a save here can be
            // refused for a rule the operator can only fix on the Bots page.
            // The link goes straight to the card that owns the first path.
            <ConfigProblems
              title="Couldn't save"
              intro={saveError}
              problems={saveProblems}
              after={
                saveProblems.length > 0 ? (
                  <Link
                    className={styles.problemLink}
                    to={settingsLink(id, saveProblems[0].path)}
                  >
                    Open Bots → Settings →
                  </Link>
                ) : null
              }
            />
          )}

          {mode === "json" ? (
            <Card className={styles.jsonCard}>
              <div className={styles.jsonHeader}>
                <h3 className={styles.jsonTitle}>Raw JSON</h3>
                <span className={styles.jsonSub}>games.config</span>
              </div>
              <div className={styles.jsonBody}>
                {jsonError && (
                  <Alert variant="danger" title="Invalid JSON">
                    {jsonError}
                  </Alert>
                )}
                <Textarea
                  aria-label="Raw config JSON"
                  value={rawJson}
                  onChange={(e) => handleRawChange(e.target.value)}
                  rows={28}
                  spellCheck={false}
                  className={styles.jsonTextarea}
                />
              </div>
            </Card>
          ) : (
            <FormsBody
              workingConfig={workingConfig}
              serverConfig={serverConfig}
              fieldErrors={fieldErrors}
              setChat={setChat}
              setGuild={setGuild}
              setHelp={setHelp}
              setGift={setGift}
              setInvites={setInvites}
              referralStats={referralQuery.data}
              referralFailed={referralQuery.isError}
              setRate={setRate}
              setFacebookAuth={setFacebookAuth}
              setProfile={setProfile}
              setCloudSave={setCloudSave}
              // Only an explicit «no» draws the warning: the flag is a fact
              // about the server, and its absence is not one.
              cloudSaveStorageConfigured={
                data.cloud_save_storage_configured !== false
              }
              setGuildAssets={setGuildAssets}
              setSigning={setSigning}
              signingStats={signingQuery.data}
              signingFailed={signingQuery.isError}
              projectName={data.name}
            />
          )}
        </div>

        <StickySaveBar
          visible={dirty}
          changes={changes}
          sections={sections}
          disabled={saveDisabled}
          message={listError}
          saving={saving}
          onDiscard={() => setDiscardOpen(true)}
          onSave={requestSave}
        />
      </div>

      <Modal
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        title="Discard unsaved changes?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDiscardOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDiscard}>
              Discard
            </Button>
          </>
        }
      >
        <p>
          You will lose {changes} {changes === 1 ? "change" : "changes"} across{" "}
          {sections} {sections === 1 ? "section" : "sections"}. This can't be
          undone.
        </p>
      </Modal>

      <Modal
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        title="Config changed by someone else"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setConflictOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConflictReload}>Reload page</Button>
          </>
        }
      >
        <p>
          Another user saved a newer version of this config. Reload to load
          their changes — your current edits will be discarded.
        </p>
      </Modal>

      <Modal
        open={enforceOpen}
        onClose={() => setEnforceOpen(false)}
        title="Turn on enforce?"
        icon={<AlertTriangle size={17} />}
        iconTone="danger"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEnforceOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setEnforceOpen(false);
                void doSave();
              }}
            >
              Turn on enforce
            </Button>
          </>
        }
      >
        {rollout ? (
          <div className={styles.signingFigures}>
            <div className={styles.signingFigureRow}>
              <span className={styles.signingFigureKey}>
                Signed, last {signingQuery.data?.window_days ?? 7} days
              </span>
              <span className={styles.signingFigureValue}>
                {formatShare(rollout.signedShare)}
              </span>
            </div>
            <div className={styles.signingFigureRow}>
              <span className={styles.signingFigureKey}>Would be refused</span>
              <span
                className={`${styles.signingFigureValue} ${styles.signingFigureBad}`}
              >
                {formatShare(rollout.refusedShare)} ·{" "}
                {formatCount(rollout.refused)}
              </span>
            </div>
          </div>
        ) : (
          <p>
            Nothing has been measured for this project, so how many requests
            would start failing is unknown.
          </p>
        )}
        <p>
          Every request that fails the check starts getting <code>401</code> the
          moment this is saved. Old builds do not degrade — they stop working,
          and their reconnect keeps knocking every 30 s until the player quits.
        </p>
        <p className={styles.signingModalMuted}>
          Switching back to <code>log</code> is one click and applies
          immediately.
        </p>
      </Modal>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Forms body — split out so the JSON mode can swap it cleanly without
// touching the save bar / modals around it.
// ──────────────────────────────────────────────────────────────────────

type FieldErrors = Partial<Record<FieldKey, string>>;

type FieldKey =
  | "chat.history_window"
  | "chat.max_message_length"
  | "guild.max_members"
  | "help_request.types"
  | "help_request.unclaimed_ttl_seconds"
  | "help_request.default_ttl_seconds"
  | "gift.types"
  | "gift.unclaimed_ttl_seconds"
  | "invites.trigger_value"
  | "invites.invitee_reward"
  | "invites.inviter_rewards"
  | "invites.reward_ttl_seconds"
  | "rate_limits.global_per_game_per_minute"
  | "rate_limits.auth_per_ip_per_minute"
  | "rate_limits.chat_per_player_per_minute"
  | "rate_limits.help_create_per_minute"
  | "rate_limits.friend_requests_per_minute"
  | "rate_limits.gifts_per_minute"
  | "rate_limits.cloud_save_per_minute"
  | "rate_limits.invites_per_minute"
  | "profile.meta_schema"
  | "cloud_save.max_bytes"
  | "request_signing.clock_skew_sec";

interface FormsBodyProps {
  workingConfig: ProjectConfig;
  serverConfig: ProjectConfig;
  fieldErrors: FieldErrors;
  setChat: <K extends keyof ChatConfig>(key: K, value: ChatConfig[K]) => void;
  setGuild: <K extends keyof GuildConfig>(
    key: K,
    value: GuildConfig[K],
  ) => void;
  setHelp: <K extends keyof HelpRequestConfig>(
    key: K,
    value: HelpRequestConfig[K],
  ) => void;
  setGift: <K extends keyof GiftConfig>(key: K, value: GiftConfig[K]) => void;
  setInvites: <K extends keyof InvitesConfig>(
    key: K,
    value: InvitesConfig[K],
  ) => void;
  /** The referral funnel. Undefined while in flight, after a failure — which
   *  `referralFailed` tells apart — and while the feature is off, when nothing
   *  was asked for in the first place. */
  referralStats: ReferralStatsData | undefined;
  referralFailed: boolean;
  setRate: <K extends keyof RateLimitsConfig>(
    key: K,
    value: RateLimitsConfig[K],
  ) => void;
  setFacebookAuth: <K extends keyof FacebookAuthConfig>(
    key: K,
    value: FacebookAuthConfig[K],
  ) => void;
  setProfile: <K extends keyof ProfileConfig>(
    key: K,
    value: ProfileConfig[K],
  ) => void;
  /** Only the profanity list's CSV export uses it — it names the file. */
  projectName: string;
  setCloudSave: <K extends keyof CloudSaveConfig>(
    key: K,
    value: CloudSaveConfig[K],
  ) => void;
  /**
   * Whether this server has a bucket. False draws the warning above the
   * section; the form stays editable either way, because a project's config
   * can be readied before ops has the bucket up.
   */
  cloudSaveStorageConfigured: boolean;
  setGuildAssets: <K extends keyof GuildAssetsConfig>(
    key: K,
    value: GuildAssetsConfig[K],
  ) => void;
  setSigning: <K extends keyof RequestSigningConfig>(
    key: K,
    value: RequestSigningConfig[K],
  ) => void;
  /** The counters, and the server's reading of the signing blob. Undefined
   *  while in flight, and after a failure — which `signingFailed` tells apart. */
  signingStats: SigningStats | undefined;
  signingFailed: boolean;
}

function FormsBody({
  workingConfig,
  serverConfig,
  fieldErrors,
  setChat,
  setGuild,
  setHelp,
  setGift,
  setInvites,
  referralStats,
  referralFailed,
  setRate,
  setFacebookAuth,
  setProfile,
  setCloudSave,
  cloudSaveStorageConfigured,
  setGuildAssets,
  setSigning,
  signingStats,
  signingFailed,
  projectName,
}: FormsBodyProps) {
  // Reveal toggle for the Facebook app secret — session-local, never persisted
  // (docs/mockups/config-auth-facebook.html L307 — trailing eye).
  const [secretRevealed, setSecretRevealed] = useState(false);

  const isModified = (a: unknown, b: unknown) => !isEqual(a, b);

  // ── Request signing ──────────────────────────────────────────────────
  // What the CONTROLS show is the blob, always. What the lines beneath them say
  // is what the server's parser makes of it — the two differ exactly when the
  // operator wrote something the parser overruled, and this section is where
  // they find that out.
  const savedSigning = serverConfig.request_signing;
  const savedSigningMode = String(savedSigning.mode);
  const workingSigningMode = String(workingConfig.request_signing.mode);
  const shownSkew: unknown = workingConfig.request_signing.clock_skew_sec;
  const signingCfg = signingStats?.config;
  /**
   * The mode actually running, read off the blob this page is holding rather
   * than off the counters' answer.
   *
   * The rule is the server's and it is one line long — a word that is not one
   * of the three is `off` (§7.1) — and taking it from the blob keeps it in step
   * with what the page shows. The counters' answer is a snapshot of an earlier
   * read of the same blob, and after a save it describes the version that was
   * replaced.
   */
  const signingModeInEffect = SIGNING_MODES.includes(
    savedSigningMode as (typeof SIGNING_MODES)[number],
  )
    ? savedSigningMode
    : "off";
  /**
   * Each flag is shown only while the control is still showing a value the flag
   * COULD be about.
   *
   * The gate is a property of what is on screen, not "has this field been
   * edited yet": the flags arrive from a second request, and the two answers
   * are refreshed by different things at different moments. Gated on equality
   * with the saved value, a save that fixed a typo would print «Config says log
   * — there is no such mode» for as long as the refetch took. Gated on the
   * value itself, the line cannot contradict the control above it, whatever
   * order the two queries land in.
   */
  const modeIsAWord = SIGNING_MODES.includes(
    workingSigningMode as (typeof SIGNING_MODES)[number],
  );
  //
  // The parser's flag is the source while there is one. When the route did not
  // answer at all there is none, and the same one-line rule of §7.1 — a word
  // that is not one of the three is `off` — stands in for it: otherwise a
  // project whose blob holds a typo would show a toggle with nothing lit and
  // nothing said, which is the mockup's picture minus the sentence that makes
  // it readable.
  const signingModeUnknown = !modeIsAWord && (signingCfg?.mode_unknown ?? true);
  const signingConfigUnreadable = !!signingCfg?.config_unreadable;
  // The number the server would have accepted: whole, and inside an int64's
  // reach — 1e30 is an integer to JavaScript and «not a whole number» to the
  // parser, and the box would otherwise show it under a line saying all is
  // well. NaN is the emptied box and is neither: the field's own «Required»
  // error speaks for it, and overrides this helper anyway.
  const shownSkewNumber =
    typeof shownSkew === "number" && Number.isSafeInteger(shownSkew)
      ? shownSkew
      : null;
  const skewIsBlank = typeof shownSkew === "number" && Number.isNaN(shownSkew);
  const signingSkewHint = signingSkewHelper(shownSkew, {
    clamped:
      !!signingCfg?.clock_skew_clamped &&
      shownSkewNumber !== null &&
      shownSkewNumber > signingCfg.clock_skew_sec,
    invalid:
      !!signingCfg?.clock_skew_invalid && shownSkewNumber === null && !skewIsBlank,
    inEffect: signingCfg?.clock_skew_sec,
  });
  /** Turning enforce ON, which is what the banner is about — not sitting in it. */
  const enteringEnforce =
    workingSigningMode === "enforce" && savedSigningMode !== "enforce";

  return (
    <>
      {/* ───────── Chat & profanity ───────── */}
      <SectionCard
        id="chat"
        title="Chat & profanity"
        apiName="games.config.chat"
        dirty={isSectionDirty(serverConfig, workingConfig, "chat")}
        description="Per-game settings for guild chat: history window, message limits, and the profanity filter that runs on every message before publish."
      >
        <FieldRow
          name="History window"
          apiName="chat.history_window"
          hint="How many recent messages per guild are retained. A background worker truncates excess every 15 minutes."
        >
          <NumberInputWithUnit
            unit="msgs"
            min={50}
            max={5000}
            value={workingConfig.chat.history_window}
            onChange={(v) => setChat("history_window", v)}
            modified={isModified(
              serverConfig.chat.history_window,
              workingConfig.chat.history_window,
            )}
            error={fieldErrors["chat.history_window"]}
            aria-label="History window"
          />
        </FieldRow>
        <FieldRow
          name="Max message length"
          apiName="chat.max_message_length"
          hint="Backend rejects chat.send if the body is longer than this."
        >
          <NumberInputWithUnit
            unit="chars"
            min={1}
            max={2000}
            value={workingConfig.chat.max_message_length}
            onChange={(v) => setChat("max_message_length", v)}
            modified={isModified(
              serverConfig.chat.max_message_length,
              workingConfig.chat.max_message_length,
            )}
            error={fieldErrors["chat.max_message_length"]}
            aria-label="Max message length"
          />
        </FieldRow>
        <FieldRow
          name="Profanity words"
          apiName="chat.profanity_words"
          hint="Custom list merged with the platform's built-in set. Case-insensitive, whole-word match."
        >
          <ProfanityWordsField
            value={workingConfig.chat.profanity_words}
            serverValue={serverConfig.chat.profanity_words}
            projectName={projectName}
            onChange={(next) => setChat("profanity_words", next)}
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Guild defaults ───────── */}
      <SectionCard
        id="guild"
        title="Guild defaults"
        apiName="games.config.guild"
        dirty={isSectionDirty(serverConfig, workingConfig, "guild")}
        description="Defaults applied when a new guild is created. Existing guilds are not retroactively updated."
      >
        <FieldRow
          name="Max members"
          apiName="guild.max_members"
          hint="Player cap per guild."
        >
          <NumberInputWithUnit
            unit="players"
            min={1}
            max={1000}
            value={workingConfig.guild.max_members}
            onChange={(v) => setGuild("max_members", v)}
            modified={isModified(
              serverConfig.guild.max_members,
              workingConfig.guild.max_members,
            )}
            error={fieldErrors["guild.max_members"]}
            aria-label="Max members"
          />
        </FieldRow>
        <FieldRow
          name="Require unique name"
          apiName="guild.require_unique_name"
          hint="When enabled, the backend rejects guild creates with a duplicate name (case-insensitive)."
        >
          <Switch
            checked={workingConfig.guild.require_unique_name}
            onChange={(next) => setGuild("require_unique_name", next)}
            label={
              workingConfig.guild.require_unique_name
                ? "On — names must be unique (case-insensitive)"
                : "Off — duplicate names allowed"
            }
          />
        </FieldRow>
        <FieldRow
          name="Metadata schema"
          apiName="guild.metadata_schema"
          hint="Custom fields you can write to guilds.metadata (JSONB). Used by search filters."
        >
          <SchemaEditor
            value={workingConfig.guild.metadata_schema}
            onChange={(next) => setGuild("metadata_schema", next)}
          />
        </FieldRow>
        <FieldRow
          name="Recommendation requirement"
          apiName="guild.recommendations"
          hint={
            <>
              Numeric gate for <code>GET /v1/guilds/recommended</code>: guilds
              the player doesn't qualify for are hidden, and the closer the
              requirement is to the player's own value, the higher the guild
              ranks. Leave unset to skip this criterion.
            </>
          }
        >
          <RecommendationRequirementField
            value={workingConfig.guild.recommendations}
            guildSchema={workingConfig.guild.metadata_schema}
            statsSchema={workingConfig.profile.stats_schema}
            onChange={(next) => setGuild("recommendations", next)}
          />
        </FieldRow>
        <FieldRow
          name="Succession level"
          apiName="guild.succession_level_key"
          hint={
            <>
              Which <code>players.stats</code> field ranks members when the
              leader's seat falls vacant — they left the guild, or their account
              was deleted. Officers still inherit before plain members. Leave
              unset to go by length of service alone.
            </>
          }
        >
          <SuccessionLevelField
            value={workingConfig.guild.succession_level_key}
            statsSchema={workingConfig.profile.stats_schema}
            onChange={(next) => setGuild("succession_level_key", next)}
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Help requests ───────── */}
      <SectionCard
        id="help_request"
        title="Help requests"
        apiName="games.config.help_request"
        dirty={isSectionDirty(serverConfig, workingConfig, "help_request")}
        description="In-guild help requests such as “need cards” or “need lives”. Per-game configuration. A request type that is not listed here cannot be created."
      >
        <FieldRow
          name="Request types"
          apiName="help_request.types"
          stacked
          hint={
            <>
              Every <code>request_type</code> the API accepts, and the rules it
              comes with: how the player takes what was donated, how often they
              may ask, how much one request may gather, how long it stays open,
              what a donor gets back — and <b>what the request has to say about
              itself</b>. A type with no payload schema accepts any payload,
              exactly as before.
            </>
          }
        >
          <HelpTypesEditor
            value={workingConfig.help_request.types}
            onChange={(next) => setHelp("types", next)}
            // The working value, not the saved one: a designer who has just
            // raised the default TTL below must see what the rows without a
            // `ttl` of their own will run on once they press Save.
            defaultTtlSeconds={workingConfig.help_request.default_ttl_seconds}
          />
        </FieldRow>
        <FieldRow
          name="Unclaimed lifetime"
          apiName="help_request.unclaimed_ttl_seconds"
          hint="How long a donation stays takeable after it arrives. Counted from the donation, not from the request — what is left when it runs out burns."
        >
          <NumberInputWithUnit
            unit="sec"
            min={TTL_MIN}
            max={TTL_MAX}
            value={workingConfig.help_request.unclaimed_ttl_seconds}
            onChange={(v) => setHelp("unclaimed_ttl_seconds", v)}
            // 0 is the backend's "never burns", and ∞ is how it is said here.
            // The floor stays on the number — a minute — because ∞ is a mode
            // and not something typed into the box.
            never={{
              fallback: DEFAULT_CONFIG.help_request.unclaimed_ttl_seconds,
            }}
            modified={isModified(
              serverConfig.help_request.unclaimed_ttl_seconds,
              workingConfig.help_request.unclaimed_ttl_seconds,
            )}
            error={fieldErrors["help_request.unclaimed_ttl_seconds"]}
            helperText={
              workingConfig.help_request.unclaimed_ttl_seconds === 0
                ? "Never expires — unclaimed donations wait indefinitely"
                : `= ${humanizeDuration(
                    workingConfig.help_request.unclaimed_ttl_seconds,
                  )}`
            }
            aria-label="Unclaimed lifetime"
          />
        </FieldRow>
        <FieldRow
          name="Default TTL"
          apiName="help_request.default_ttl_seconds"
          hint={
            <>
              How long a request stays open when its type sets no{" "}
              <code>ttl</code> of its own.
            </>
          }
        >
          <NumberInputWithUnit
            unit="sec"
            min={TTL_MIN}
            max={TTL_MAX}
            value={workingConfig.help_request.default_ttl_seconds}
            onChange={(v) => setHelp("default_ttl_seconds", v)}
            never={{
              fallback: DEFAULT_CONFIG.help_request.default_ttl_seconds,
            }}
            modified={isModified(
              serverConfig.help_request.default_ttl_seconds,
              workingConfig.help_request.default_ttl_seconds,
            )}
            error={fieldErrors["help_request.default_ttl_seconds"]}
            // The types that set no `ttl` of their own inherit this, so ∞ here
            // is what makes a whole project's requests eternal at once.
            helperText={
              workingConfig.help_request.default_ttl_seconds === 0
                ? "Never expires — requests stay open until fulfilled or cancelled"
                : `= ${humanizeDuration(
                    workingConfig.help_request.default_ttl_seconds,
                  )}`
            }
            aria-label="Default TTL"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Gifts ───────── */}
      <SectionCard
        id="gift"
        title="Gifts"
        apiName="games.config.gift"
        dirty={isSectionDirty(serverConfig, workingConfig, "gift")}
        description="A player sending a card or a life straight to another player — no request asked for it. The gift waits in the recipient's inbox until they take it. Nothing is debited from the sender: the quotas below are the whole brake. With no type listed, gifts are off for this project and every send is refused."
      >
        <FieldRow
          name="Channels"
          apiName="gift.channels"
          hint={
            <>
              Who a player is allowed to send to. Checked when the gift is sent,
              never again — leaving the guild afterwards does not take back what
              already arrived. A send outside every enabled channel is{" "}
              <code>gift_recipient_not_eligible</code>.
            </>
          }
        >
          <GiftChannelsField
            value={workingConfig.gift.channels}
            onChange={(next) => setGift("channels", next)}
          />
        </FieldRow>
        <FieldRow
          name="Gift types"
          apiName="gift.types"
          stacked
          hint={
            <>
              Every <code>type</code> the API accepts, and the rules it comes
              with: how the recipient takes it out of their inbox, how often the
              sender may give it away, how much may ride on one send, and how
              often the same pair of players may repeat it. The game sends the
              type name and nothing else.
            </>
          }
        >
          <GiftTypesEditor
            value={workingConfig.gift.types}
            onChange={(next) => setGift("types", next)}
          />
        </FieldRow>
        <FieldRow
          name="Unclaimed lifetime"
          apiName="gift.unclaimed_ttl_seconds"
          hint="How long a gift stays takeable after it arrives. Counted from the send; what is left when it runs out burns. Press ∞ for a gift that waits indefinitely."
        >
          <NumberInputWithUnit
            unit="sec"
            min={TTL_MIN}
            max={TTL_MAX}
            value={workingConfig.gift.unclaimed_ttl_seconds}
            onChange={(v) => setGift("unclaimed_ttl_seconds", v)}
            never={{ fallback: DEFAULT_CONFIG.gift.unclaimed_ttl_seconds }}
            modified={isModified(
              serverConfig.gift.unclaimed_ttl_seconds,
              workingConfig.gift.unclaimed_ttl_seconds,
            )}
            error={fieldErrors["gift.unclaimed_ttl_seconds"]}
            helperText={
              workingConfig.gift.unclaimed_ttl_seconds === 0
                ? "Never expires — an unclaimed gift waits indefinitely"
                : `= ${humanizeDuration(
                    workingConfig.gift.unclaimed_ttl_seconds,
                  )}`
            }
            // The row is called «Unclaimed lifetime» (mockup L458), and so is
            // the one in the section above it. The NAME has to part company
            // there: two fields called the same thing on one page are one field
            // to anybody not looking at the cards, and the ∞ toggle borrows
            // this string for its own button — where telling four identical
            // «never expires» buttons apart is all a screen reader has.
            aria-label="Gift unclaimed lifetime"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Invites (t-162 config-invites.html, t-164 config-invites-ladder.html) ───────── */}
      <SectionCard
        id="invites"
        title="Invites"
        apiName="games.config.invites"
        dirty={isSectionDirty(serverConfig, workingConfig, "invites")}
        description="A player shares their friend code; whoever installs the game through it and reaches the trigger below earns a reward — and the player who invited them climbs a ladder of their own, one step for every friend who gets there. One inviter per player, set once and never changed. Every reward arrives in the inbox, next to leaderboard grants and gifts."
      >
        {/* Above the rows rather than in one: read-only numbers have no config
            key for a FieldRow's left column to promise. They stay in this card
            because the trigger and the ladder are chosen by them. Only for a
            project that has the mechanic ON — see the query. */}
        {serverConfig.invites.enabled && (
          <ReferralStats stats={referralStats} failed={referralFailed} />
        )}
        <FieldRow
          name="Enabled"
          apiName="invites.enabled"
          hint={
            <>
              Off by default. While off, all three <code>/v1/invites/*</code>{" "}
              endpoints answer <code>403 invite_disabled</code>, and nothing is
              granted — referrals already accepted stay.
            </>
          }
        >
          <Switch
            checked={workingConfig.invites.enabled}
            onChange={(next) => setInvites("enabled", next)}
            label={
              workingConfig.invites.enabled
                ? "On — players can accept a friend code"
                : "Off — all three endpoints answer invite_disabled"
            }
            // The visible label flips with the state, which aria-checked
            // already conveys — name the control after the row instead.
            aria-label="Invites"
          />
        </FieldRow>
        <FieldRow
          name="Reward trigger"
          apiName="invites.trigger_stat_key · invites.trigger_value"
          hint={
            <>
              An <code>int</code> field of <code>profile.stats_schema</code> and
              the value it has to reach. Checked whenever the game writes stats
              (<code>PATCH /v1/me/stats</code>), in the raw units the game
              stores — not the number on the player&apos;s screen.
            </>
          }
        >
          <InviteTriggerField
            statKey={workingConfig.invites.trigger_stat_key}
            triggerValue={workingConfig.invites.trigger_value}
            statsSchema={workingConfig.profile.stats_schema}
            onChange={(next) => {
              setInvites("trigger_stat_key", next.statKey);
              setInvites("trigger_value", next.triggerValue);
            }}
            error={fieldErrors["invites.trigger_value"]}
          />
        </FieldRow>
        <FieldRow
          name="Invitee reward"
          apiName="invites.invitee_reward_name · invites.invitee_reward"
          stacked
          hint={
            <>
              What the invited player gets for reaching the trigger — the same
              whichever step their inviter is on. The game receives it from{" "}
              <code>/referral</code> and <code>/state</code> and draws its own
              popup from it.
            </>
          }
        >
          <InviteeRewardEditor
            name={workingConfig.invites.invitee_reward_name}
            reward={workingConfig.invites.invitee_reward}
            nameModified={isModified(
              serverConfig.invites.invitee_reward_name,
              workingConfig.invites.invitee_reward_name,
            )}
            rewardModified={isModified(
              serverConfig.invites.invitee_reward,
              workingConfig.invites.invitee_reward,
            )}
            onNameChange={(next) => setInvites("invitee_reward_name", next)}
            onRewardChange={(next) => setInvites("invitee_reward", next)}
          />
        </FieldRow>
        <FieldRow
          name="Inviter rewards"
          apiName="invites.inviter_rewards"
          stacked
          hint="What the player who shared the code gets for each friend who reaches the trigger — friend 1 is the first of them to get there, whatever order the codes were accepted in. A number no step covers pays the inviter nothing; the invited player is paid regardless. Friends who reach the trigger while the list is empty are not counted."
        >
          <InviterRewardsEditor
            value={workingConfig.invites.inviter_rewards}
            saved={serverConfig.invites.inviter_rewards}
            onChange={(next) => setInvites("inviter_rewards", next)}
          />
        </FieldRow>
        <FieldRow
          name="Unclaimed lifetime"
          apiName="invites.reward_ttl_seconds"
          hint="How long a granted reward waits in the inbox before what is left burns — for both sides. Counted from the grant."
        >
          <NumberInputWithUnit
            unit="sec"
            min={TTL_MIN}
            max={TTL_MAX}
            value={workingConfig.invites.reward_ttl_seconds}
            onChange={(v) => setInvites("reward_ttl_seconds", v)}
            // The key's own default is `0`, so ∞ has no stored number to put
            // back: seven days, the length the other two sections already
            // treat as a reasonable wait for a player who took a week off.
            never={{ fallback: 604800 }}
            modified={isModified(
              serverConfig.invites.reward_ttl_seconds,
              workingConfig.invites.reward_ttl_seconds,
            )}
            error={fieldErrors["invites.reward_ttl_seconds"]}
            helperText={
              workingConfig.invites.reward_ttl_seconds === 0 ? (
                <>
                  <code>0</code> — never burns, which is what ∞ writes.
                </>
              ) : (
                <>
                  {humanizeDuration(
                    workingConfig.invites.reward_ttl_seconds,
                  )}
                  . <code>0</code> — never burns, which is what ∞ writes.
                </>
              )
            }
            // Three rows on this page are called «Unclaimed lifetime», and the
            // ∞ button borrows this string: telling the identical buttons
            // apart is all a screen reader has.
            aria-label="Invite unclaimed lifetime"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Rate limits ───────── */}
      <SectionCard
        id="rate_limits"
        title="Rate limits"
        apiName="games.config.rate_limits"
        dirty={isSectionDirty(serverConfig, workingConfig, "rate_limits")}
        description="Every limit the platform enforces, from the widest to the narrowest. Token-bucket implemented in Redis via Lua; each has a bucket of its own, so one feature's traffic never spends another's. Defaults are conservative — raise carefully."
      >
        {/*
          Every row names its own default, so that "is 1000 what I chose or what
          I was given?" has an answer in the row rather than in the docs. EVERY
          row, not only the ones a given release added: a blob with no
          `rate_limits` object at all — a project never saved through this page,
          or one restored from an older export — hands Auth and help-create
          their defaults on exactly the same terms. No count here on purpose;
          the last one went stale the first time a key was added.
        */}
        <FieldRow
          name="Global per game / min"
          apiName="rate_limits.global_per_game_per_minute"
          hint="Every authenticated request this project makes, counted across all its players. The ceiling on what one project can ask of the platform — it refuses before any of the per-player limits below are reached."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={100000}
            value={workingConfig.rate_limits.global_per_game_per_minute}
            onChange={(v) => setRate("global_per_game_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.global_per_game_per_minute,
              workingConfig.rate_limits.global_per_game_per_minute,
            )}
            error={fieldErrors["rate_limits.global_per_game_per_minute"]}
            helperText="Default 1000"
            aria-label="Global per game per minute"
          />
        </FieldRow>
        <FieldRow
          name="Auth per IP / min"
          apiName="rate_limits.auth_per_ip_per_minute"
          hint="Brute-force protection on login + anonymous endpoints."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={10000}
            value={workingConfig.rate_limits.auth_per_ip_per_minute}
            onChange={(v) => setRate("auth_per_ip_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.auth_per_ip_per_minute,
              workingConfig.rate_limits.auth_per_ip_per_minute,
            )}
            error={fieldErrors["rate_limits.auth_per_ip_per_minute"]}
            helperText="Default 30"
            aria-label="Auth per IP per minute"
          />
        </FieldRow>
        <FieldRow
          name="Chat per player / min"
          apiName="rate_limits.chat_per_player_per_minute"
          hint="How many chat messages a player may send in a minute. One budget across both transports — the REST endpoints and the websocket's chat.send frame count into it together."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.chat_per_player_per_minute}
            onChange={(v) => setRate("chat_per_player_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.chat_per_player_per_minute,
              workingConfig.rate_limits.chat_per_player_per_minute,
            )}
            error={fieldErrors["rate_limits.chat_per_player_per_minute"]}
            helperText="Default 20"
            aria-label="Chat per player per minute"
          />
        </FieldRow>
        <FieldRow
          name="Help-create per player / min"
          apiName="rate_limits.help_create_per_minute"
          hint="How many new help requests a single player can create per minute. Creating only — reading the board, reading one's own quota and donating to somebody else's request cost nothing here."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.help_create_per_minute}
            onChange={(v) => setRate("help_create_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.help_create_per_minute,
              workingConfig.rate_limits.help_create_per_minute,
            )}
            error={fieldErrors["rate_limits.help_create_per_minute"]}
            helperText="Default 10"
            aria-label="Help create per minute"
          />
        </FieldRow>
        <FieldRow
          name="Friend requests per player / min"
          apiName="rate_limits.friend_requests_per_minute"
          hint="How many friend requests one player may send in a minute, importing from Facebook included. With no block list in this feature, this bucket is the whole anti-spam story."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.friend_requests_per_minute}
            onChange={(v) => setRate("friend_requests_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.friend_requests_per_minute,
              workingConfig.rate_limits.friend_requests_per_minute,
            )}
            error={fieldErrors["rate_limits.friend_requests_per_minute"]}
            helperText="Default 10"
            aria-label="Friend requests per minute"
          />
        </FieldRow>
        <FieldRow
          name="Gifts per player / min"
          apiName="rate_limits.gifts_per_minute"
          hint="How many gifts one player may send in a minute. A machine brake against loops and scripts, not a game rule — the game rule is the quota on the type. Same shape and default as the help-create bucket above it."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.gifts_per_minute}
            onChange={(v) => setRate("gifts_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.gifts_per_minute,
              workingConfig.rate_limits.gifts_per_minute,
            )}
            error={fieldErrors["rate_limits.gifts_per_minute"]}
            helperText="Default 10"
            aria-label="Gifts per minute"
          />
        </FieldRow>
        <FieldRow
          name="Cloud-save writes per player / min"
          apiName="rate_limits.cloud_save_per_minute"
          hint="Upload-URL requests, finalize calls and deletes. Status checks and download URLs ride on the global per-game cap."
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.cloud_save_per_minute}
            onChange={(v) => setRate("cloud_save_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.cloud_save_per_minute,
              workingConfig.rate_limits.cloud_save_per_minute,
            )}
            error={fieldErrors["rate_limits.cloud_save_per_minute"]}
            helperText="Default 10"
            aria-label="Cloud-save writes per minute"
          />
        </FieldRow>
        <FieldRow
          name="Invite codes per minute"
          apiName="rate_limits.invites_per_minute"
          hint={
            <>
              Attempts to accept a code, per player. Same bucket family as{" "}
              <code>friend_requests_per_minute</code>; over it the API answers{" "}
              <code>429 rate_limited</code>, which the client retries later.
            </>
          }
        >
          <NumberInputWithUnit
            unit="/min"
            min={1}
            max={600}
            value={workingConfig.rate_limits.invites_per_minute}
            onChange={(v) => setRate("invites_per_minute", v)}
            modified={isModified(
              serverConfig.rate_limits.invites_per_minute,
              workingConfig.rate_limits.invites_per_minute,
            )}
            error={fieldErrors["rate_limits.invites_per_minute"]}
            helperText="Default 10"
            aria-label="Invite codes per minute"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Authentication ───────── */}
      <SectionCard
        id="auth"
        title="Authentication"
        apiName="games.config.auth"
        dirty={isSectionDirty(serverConfig, workingConfig, "auth")}
        description="Social sign-in for players. Credentials are used server-side only — to verify tokens issued by the provider SDK and to fetch profile avatars. Supports both classic access tokens and iOS Limited Login (JWT)."
      >
        <FieldRow
          name="Facebook login"
          apiName="auth.facebook.enabled"
          hint={
            <>
              Master switch. When off, Facebook login and linking return{" "}
              <code>facebook_login_disabled</code>.
            </>
          }
        >
          <Switch
            checked={workingConfig.auth.facebook.enabled}
            onChange={(next) => setFacebookAuth("enabled", next)}
            label={workingConfig.auth.facebook.enabled ? "Enabled" : "Disabled"}
            // The visible label flips with the state, which aria-checked
            // already conveys — name the control after the row instead.
            aria-label="Facebook login"
          />
        </FieldRow>
        <FieldRow
          name="App ID"
          apiName="auth.facebook.app_id"
          hint="From developers.facebook.com → your app → Settings → Basic."
        >
          <div className={styles.authField}>
            <Input
              className={styles.monoInput}
              value={workingConfig.auth.facebook.app_id}
              onChange={(e) => setFacebookAuth("app_id", e.target.value)}
              modified={isModified(
                serverConfig.auth.facebook.app_id,
                workingConfig.auth.facebook.app_id,
              )}
              aria-label="App ID"
            />
          </div>
        </FieldRow>
        <FieldRow
          name="App secret"
          apiName="auth.facebook.app_secret"
          hint={
            <>
              Needed for classic-token verification via Graph{" "}
              <code>/debug_token</code>. Limited Login (JWT) works without it.
            </>
          }
        >
          <div className={styles.authField}>
            <Input
              className={styles.monoInput}
              type={secretRevealed ? "text" : "password"}
              // Not a credential of the signed-in admin — keep password
              // managers from autofilling over the project's app secret.
              // Chrome ignores "off" on password fields but honours this.
              autoComplete="new-password"
              aria-describedby={SECRET_HELPER_ID}
              value={workingConfig.auth.facebook.app_secret}
              onChange={(e) => setFacebookAuth("app_secret", e.target.value)}
              modified={isModified(
                serverConfig.auth.facebook.app_secret,
                workingConfig.auth.facebook.app_secret,
              )}
              aria-label="App secret"
              trailingIcon={
                <button
                  type="button"
                  className={styles.toggleVis}
                  aria-label={
                    secretRevealed ? "Hide app secret" : "Reveal app secret"
                  }
                  title={secretRevealed ? "Hide" : "Reveal"}
                  onClick={() => setSecretRevealed((v) => !v)}
                  tabIndex={-1}
                >
                  {secretRevealed ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              }
            />
          </div>
          <div id={SECRET_HELPER_ID} className={styles.authHelper}>
            Stored in <code>games.config</code> — same policy as the project API
            key (plaintext, retrievable by project admins).
          </div>
        </FieldRow>
      </SectionCard>

      {/* ───────── Profile ───────── */}
      <SectionCard
        id="profile"
        title="Profile"
        apiName="games.config.profile"
        dirty={isSectionDirty(serverConfig, workingConfig, "profile")}
        description="What players can store in their profiles and which avatars are available."
      >
        <FieldRow
          name="Stats schema"
          apiName="profile.stats_schema"
          hint="Custom fields in players.stats (JSONB). Backend validates types and rejects unknown keys."
        >
          <SchemaEditor
            value={workingConfig.profile.stats_schema}
            onChange={(next) => setProfile("stats_schema", next)}
          />
        </FieldRow>
        <FieldRow
          name="Meta schema"
          apiName="profile.meta_schema"
          hint="Cosmetic profile fields in players.meta (JSONB) — avatar frame, badges, title. Backend validates the type and, when the allow-list is non-empty, the value itself."
        >
          <MetaSchemaEditor
            value={workingConfig.profile.meta_schema}
            onChange={(next) => setProfile("meta_schema", next)}
            helperText={metaSchemaHelper(workingConfig.profile.meta_schema)}
          />
        </FieldRow>
        <FieldRow
          name="Allowed avatars"
          apiName="profile.allowed_avatars"
          hint="Whitelist of asset IDs. Empty = no validation (any string accepted)."
        >
          <ChipInput
            value={workingConfig.profile.allowed_avatars}
            onChange={(next) => setProfile("allowed_avatars", next)}
            placeholder="Add asset ID…"
            modified={isModified(
              serverConfig.profile.allowed_avatars,
              workingConfig.profile.allowed_avatars,
            )}
            validate={(chip) =>
              AVATAR_REGEX.test(chip)
                ? null
                : "Letters, digits, underscore only"
            }
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Cloud saves (t-156 config-cloud-saves.html) ───────── */}
      <SectionCard
        id="cloud_save"
        title="Cloud saves"
        apiName="games.config.cloud_save"
        dirty={isSectionDirty(serverConfig, workingConfig, "cloud_save")}
        notice={
          !cloudSaveStorageConfigured && (
            <Alert
              variant="warn"
              title="Storage is not configured on this server"
            >
              <code>SH_GCS_BUCKET</code> is empty, so the game API answers{" "}
              <code>503 cloud_save_unavailable</code> whatever this switch says,
              and the player card cannot download or upload. Set the bucket and
              the service-account key on the server —{" "}
              <code>docs/operations.md</code>, «Cloud saves».
            </Alert>
          )
        }
        description="One save per player, kept in the platform's Google Cloud Storage bucket. The game uploads and downloads it straight from the bucket through short-lived signed URLs; the server only tracks what is there and hands out the URLs. Operators see and replace a player's save on the player card."
      >
        <FieldRow
          name="Enabled"
          apiName="cloud_save.enabled"
          hint={
            <>
              Off by default. While off, every <code>/v1/me/cloud-save</code>{" "}
              call answers <code>403 cloud_save_disabled</code> and the player
              card hides its Cloud save section; saves already in the bucket
              stay.
            </>
          }
        >
          <Switch
            checked={workingConfig.cloud_save.enabled}
            onChange={(next) => setCloudSave("enabled", next)}
            label={
              workingConfig.cloud_save.enabled
                ? "On — players can upload and download their save"
                : "Off — the game API answers cloud_save_disabled"
            }
            // The visible label flips with the state, which aria-checked
            // already conveys — name the control after the row instead.
            aria-label="Cloud saves"
          />
        </FieldRow>
        <FieldRow
          name="Max save size"
          apiName="cloud_save.max_bytes"
          hint="Ceiling for one save as stored — compressed, when the SDK compresses (it does by default). 1–64 MiB. Baked into every signed upload URL, so an oversized upload is refused by the bucket itself, and checked again on an admin upload."
        >
          <NumberInputWithUnit
            unit="MiB"
            min={CLOUD_SAVE_MIN_MIB}
            max={CLOUD_SAVE_MAX_MIB}
            step={1}
            value={bytesToMiB(workingConfig.cloud_save.max_bytes)}
            onChange={(v) => setCloudSave("max_bytes", mibToBytes(v))}
            modified={isModified(
              serverConfig.cloud_save.max_bytes,
              workingConfig.cloud_save.max_bytes,
            )}
            error={fieldErrors["cloud_save.max_bytes"]}
            helperText={
              Number.isFinite(workingConfig.cloud_save.max_bytes)
                ? `Stored as bytes: ${formatCount(workingConfig.cloud_save.max_bytes)}.`
                : undefined
            }
            aria-label="Max save size"
          />
        </FieldRow>
      </SectionCard>

      {/* ───────── Guild assets ───────── */}
      <SectionCard
        id="guild_assets"
        title="Guild assets"
        apiName="games.config.guild_assets"
        dirty={isSectionDirty(serverConfig, workingConfig, "guild_assets")}
      >
        <FieldRow
          name="Allowed logos"
          apiName="guild_assets.allowed_logos"
          hint="Whitelist of guild logo asset IDs. Empty — accept any."
        >
          <ChipInput
            value={workingConfig.guild_assets.allowed_logos}
            onChange={(next) => setGuildAssets("allowed_logos", next)}
            placeholder="Add asset ID…"
            modified={isModified(
              serverConfig.guild_assets.allowed_logos,
              workingConfig.guild_assets.allowed_logos,
            )}
            validate={(chip) =>
              AVATAR_REGEX.test(chip)
                ? null
                : "Letters, digits, underscore only"
            }
          />
        </FieldRow>
      </SectionCard>

      <SectionCard
        id="request_signing"
        title="Request signing"
        apiName="games.config.request_signing"
        dirty={isSectionDirty(serverConfig, workingConfig, "request_signing")}
        description="Every authenticated request carries a signature derived from the player's session key. Mode changes apply to REST immediately; live WebSocket connections keep the mode they were opened with."
      >
        {/* Above the rows rather than in one: the numbers are read-only, and a
            FieldRow's left column would promise them a config key they do not
            have. They stay in this card because the enforce decision is made by
            them (§8.3). */}
        <RequestSigningStats
          stats={signingStats}
          failed={signingFailed}
          mode={signingModeInEffect}
          clockSkewSec={signingStats?.config.clock_skew_sec ?? 300}
        />
        <FieldRow
          name="Mode"
          apiName="request_signing.mode"
          hint={SIGNING_MODE_HINT[workingSigningMode] ?? SIGNING_MODE_HINT.off}
        >
          <div>
            <SegmentedToggle<string>
              aria-label="Request signing mode"
              value={workingSigningMode}
              onChange={(next) => setSigning("mode", next)}
              options={SIGNING_MODES.map((m) => ({ value: m, label: m }))}
            />
          </div>
          {signingModeUnknown && (
            // No segment is lit, and that takes no change to the toggle: a
            // value matching none of the options leaves all three alone, which
            // is the honest picture of "nobody chose this".
            <div className={styles.signingHelperWarn}>
              Config says <code>{workingSigningMode}</code> — there is no such
              mode, so <code>off</code> is in effect. Pick one to fix it.
            </div>
          )}
          {signingConfigUnreadable && (
            // Not one of the parser's three flags — it never gets far enough to
            // raise one. It is said here all the same, because the game API
            // does not read this project as off: it lets every request through
            // unchecked, and a form quietly showing `off` would be the one
            // screen that could have said so.
            <div className={styles.signingHelperWarn}>
              This project&apos;s config does not parse, so none of it is
              applied — signed or not, every request goes through unchecked.
              Fix the JSON in <b>Raw JSON</b> above; the mode below takes effect
              again as soon as it parses.
            </div>
          )}
          {enteringEnforce && (
            <EnforceWarning
              rollout={signingRollout(signingStats)}
              windowDays={signingStats?.window_days ?? 7}
              missing={signingStats?.by_result.missing ?? 0}
              fromMode={signingModeInEffect}
            />
          )}
        </FieldRow>
        <FieldRow
          name="Clock skew"
          apiName="request_signing.clock_skew_sec"
          hint="Half the accepted timestamp window. A request is accepted while its timestamp is within ± this of server time."
        >
          <NumberInputWithUnit
            unit="sec"
            // A string in the blob has no number to show, so the box is empty
            // and the helper below names what is written there. What it must
            // NOT do is show the default as though it were the setting.
            value={
              typeof workingConfig.request_signing.clock_skew_sec === "number"
                ? workingConfig.request_signing.clock_skew_sec
                : NaN
            }
            onChange={(v) => setSigning("clock_skew_sec", v)}
            modified={isModified(
              serverConfig.request_signing.clock_skew_sec,
              workingConfig.request_signing.clock_skew_sec,
            )}
            error={fieldErrors["request_signing.clock_skew_sec"]}
            helperVariant={signingSkewHint.warn ? "warn" : "default"}
            helperText={signingSkewHint.text}
            aria-label="Clock skew"
          />
        </FieldRow>
      </SectionCard>
    </>
  );
}

/**
 * The first of the two guards on enforce, and the one that arrives in time to
 * change the answer: it appears the moment the mode is picked, in the control's
 * own column, before anything is saved.
 *
 * It never refuses. The threshold decides the TONE and whether Save will ask
 * again — the studio picks its own number, and the platform's job is to make
 * sure the number it is choosing against is on the screen (§11.3).
 */
function EnforceWarning({
  rollout,
  windowDays,
  missing,
  fromMode,
}: {
  rollout: ReturnType<typeof signingRollout>;
  windowDays: number;
  /** Requests that carried no signature — how the "almost all of them" is
   *  earned rather than assumed. */
  missing: number;
  /** The mode in force right now, which is what this is a step away from. */
  fromMode: string;
}) {
  if (!rollout) {
    // The most dangerous transition of the three, and the only one where the
    // warning cannot name a number. It says exactly that instead of guessing.
    return (
      <Alert
        variant="warn"
        title={
          <>
            Going straight from <code>{fromMode}</code> to <code>enforce</code>{" "}
            — nothing has been measured
          </>
        }
      >
        <p>
          No counters exist for this project, so how many players would be
          locked out is unknown. Every build that does not sign yet stops
          working the moment this is saved, and keeps reconnecting every 30 s.
        </p>
        {fromMode === "log" ? (
          // Already in log and still nothing counted — «run log first» would be
          // telling the operator to do what they are already doing.
          <p>
            <code>log</code> is on and has counted nothing yet. Leave it there
            until the numbers above fill in — it refuses nothing while they do.
          </p>
        ) : (
          <p>
            Run <code>log</code> for a while first — it refuses nothing and
            fills the numbers above.
          </p>
        )}
        <p>
          {/* The one reassurance this warning can still offer when it has no
              number to offer, and the case that needs it most. */}
          Rolling back to <code>log</code> takes one click and applies
          immediately.
        </p>
      </Alert>
    );
  }
  const title = `${formatShare(rollout.refusedShare)} of requests would start getting 401`;
  if (rollout.signedShare >= SIGNED_SHARE_THRESHOLD) {
    // Above the threshold the number is still named: "almost none" and "none"
    // are different, and only one of them is safe to skim past.
    return (
      <Alert variant="info" title={title}>
        <p>
          {formatCount(rollout.refused)} requests over the last {windowDays}{" "}
          days. Saving applies the change to REST immediately.
        </p>
      </Alert>
    );
  }
  return (
    <Alert variant="warn" title={title}>
      <p>
        Over the last {windowDays} days that is{" "}
        <b>{formatCount(rollout.refused)} requests</b>
        {missing >= rollout.refused * 0.9
          ? ", almost all of them from builds that send no signature yet"
          : ""}
        .
      </p>
      <p>
        They will not go quiet: the SDK treats a 401 on the WebSocket handshake
        as a transient failure and keeps reconnecting every 30 s until the
        player quits the game.
      </p>
      <p>
        Rolling back to <code>log</code> takes one click and applies
        immediately.
      </p>
    </Alert>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Recommendation requirement — the `guild.metadata` ≤ `players.stats` pair
// (.codelane/artifacts/t-18/config-guild-recommendations.html L346-L378).
// ──────────────────────────────────────────────────────────────────────

interface RecommendationRequirementFieldProps {
  value: GuildRecommendationsConfig;
  guildSchema: SchemaField[];
  statsSchema: SchemaField[];
  onChange: (next: GuildRecommendationsConfig) => void;
}

function RecommendationRequirementField({
  value,
  guildSchema,
  statsSchema,
  onChange,
}: RecommendationRequirementFieldProps) {
  const reactId = useId();
  const guildFieldId = `${reactId}-guild-key`;
  const playerFieldId = `${reactId}-player-key`;
  const guildKey = value.requirement_guild_key;
  const playerKey = value.requirement_player_key;

  const guildOptions = useMemo(
    () => schemaKeyOptionsWithUnset(guildSchema, guildKey),
    [guildSchema, guildKey],
  );
  const playerOptions = useMemo(
    () => schemaKeyOptionsWithUnset(statsSchema, playerKey),
    [statsSchema, playerKey],
  );

  return (
    <>
      <div className={styles.pairGrid}>
        <div className={styles.pairCol}>
          <label className={styles.microLabel} htmlFor={guildFieldId}>
            Guild metadata key
          </label>
          <Select
            id={guildFieldId}
            className={`${styles.pairSelect} ${
              guildKey === "" ? styles.pairSelectUnset : ""
            }`}
            value={guildKey}
            onChange={(next) =>
              onChange({ ...value, requirement_guild_key: next })
            }
            options={guildOptions}
            monospaceValue={guildKey !== ""}
            placeholder="— not set —"
          />
        </div>
        <span className={styles.pairOp} aria-hidden>
          ≤
        </span>
        <div className={styles.pairCol}>
          <label className={styles.microLabel} htmlFor={playerFieldId}>
            Player stat key
          </label>
          <Select
            id={playerFieldId}
            className={`${styles.pairSelect} ${
              playerKey === "" ? styles.pairSelectUnset : ""
            }`}
            value={playerKey}
            onChange={(next) =>
              onChange({ ...value, requirement_player_key: next })
            }
            options={playerOptions}
            monospaceValue={playerKey !== ""}
            placeholder="— not set —"
          />
        </div>
      </div>
      <RequirementPreview guildKey={guildKey} playerKey={playerKey} />
    </>
  );
}

interface SuccessionLevelFieldProps {
  value: string;
  statsSchema: SchemaField[];
  onChange: (next: string) => void;
}

/**
 * The stat that ranks members when a guild's leader's seat falls vacant.
 *
 * One key, not a pair like the recommendation requirement: succession asks a
 * single question ("who is stronger"), so there is nothing to compare it
 * against and nothing to warn about — an unset key is a complete, working
 * configuration, and the row says what it means rather than flagging it.
 *
 * Which is also why it passes no `emptyLabel`: a project with no `int` stat has
 * this row already configured correctly, so there is nothing for the picker to
 * refuse. The Invites trigger, whose key is half of something, passes one.
 */
function SuccessionLevelField({
  value,
  statsSchema,
  onChange,
}: SuccessionLevelFieldProps) {
  return (
    <>
      <StatKeySelect
        value={value}
        statsSchema={statsSchema}
        onChange={onChange}
        className={styles.soloSelect}
        aria-label="Succession level key"
      />
      {value === "" ? (
        <div className={`${styles.previewBox} ${styles.previewOff}`}>
          No level configured — the seat goes to the longest-serving officer,
          then to the longest-serving member. Bots never inherit: a guild whose
          leader leaves with none but bots behind them is disbanded.
        </div>
      ) : (
        <div className={styles.previewBox}>
          The seat goes to the officer with the highest{" "}
          <code>players.stats.{value}</code>, and to the strongest plain member
          when the guild has no officers. Members the stat says nothing about
          rank last. Bots never inherit: a guild whose leader leaves with none
          but bots behind them is disbanded.
        </div>
      )}
    </>
  );
}

/**
 * Spells out what the pair currently means. Half a pair is a warning, not an
 * error — the config saves fine, the criterion just never runs, so it must not
 * reach `fieldErrors` and block the save bar.
 */
function RequirementPreview({
  guildKey,
  playerKey,
}: {
  guildKey: string;
  playerKey: string;
}) {
  if (guildKey !== "" && playerKey !== "") {
    return (
      <div className={styles.previewBox}>
        A guild is recommended when <code>guilds.metadata.{guildKey}</code> ≤{" "}
        <code>players.stats.{playerKey}</code>. Guilds that don't carry the key
        are never filtered out.
      </div>
    );
  }
  if (guildKey === "" && playerKey === "") {
    return (
      <div className={`${styles.previewBox} ${styles.previewOff}`}>
        No requirement configured — recommendations rank on country, free slots,
        fill rate and activity only.
      </div>
    );
  }
  return (
    <div className={`${styles.previewBox} ${styles.previewWarn}`}>
      Pick both keys — with only one side the requirement is ignored.
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function validateConfig(cfg: ProjectConfig): FieldErrors {
  return {
    "chat.history_window":
      rangeError(cfg.chat.history_window, 50, 5000) ?? undefined,
    "chat.max_message_length":
      rangeError(cfg.chat.max_message_length, 1, 2000) ?? undefined,
    "guild.max_members":
      rangeError(cfg.guild.max_members, 1, 1000) ?? undefined,
    "request_signing.clock_skew_sec": signingSkewError(
      cfg.request_signing.clock_skew_sec,
    ),
    "help_request.types": helpTypesError(cfg.help_request.types),
    "help_request.unclaimed_ttl_seconds": ttlFieldError(
      cfg.help_request.unclaimed_ttl_seconds,
    ),
    "help_request.default_ttl_seconds": ttlFieldError(
      cfg.help_request.default_ttl_seconds,
    ),
    "gift.types": giftTypesError(cfg.gift.types),
    // The same `0`-is-∞ reading, and the same span, as the durations of the
    // section above: one idea of a sane length of time for the whole page.
    "gift.unclaimed_ttl_seconds": ttlFieldError(cfg.gift.unclaimed_ttl_seconds),
    // Always checked, including with no key: clearing the key resets the
    // number to 0 (see InviteTriggerField), so the only way to be out of range
    // without a key is a Raw JSON save — exactly the case an unchecked field
    // would let through in silence. `0` passes: it is the "no threshold" the
    // row warns about rather than refuses.
    "invites.trigger_value":
      rangeError(cfg.invites.trigger_value, 0, TRIGGER_VALUE_MAX) ?? undefined,
    "invites.invitee_reward": rewardFieldError(
      cfg.invites.invitee_reward,
      "Invitee reward",
    ),
    "invites.inviter_rewards": inviterRewardsError(cfg.invites.inviter_rewards),
    // The same `0`-is-∞ reading, and the same span, as the two sections above.
    "invites.reward_ttl_seconds": ttlFieldError(cfg.invites.reward_ttl_seconds),
    "rate_limits.global_per_game_per_minute":
      rangeError(cfg.rate_limits.global_per_game_per_minute, 1, 100000) ??
      undefined,
    "rate_limits.auth_per_ip_per_minute":
      rangeError(cfg.rate_limits.auth_per_ip_per_minute, 1, 10000) ?? undefined,
    "rate_limits.chat_per_player_per_minute":
      rangeError(cfg.rate_limits.chat_per_player_per_minute, 1, 600) ??
      undefined,
    "rate_limits.help_create_per_minute":
      rangeError(cfg.rate_limits.help_create_per_minute, 1, 600) ?? undefined,
    "rate_limits.friend_requests_per_minute":
      rangeError(cfg.rate_limits.friend_requests_per_minute, 1, 600) ??
      undefined,
    "rate_limits.gifts_per_minute":
      rangeError(cfg.rate_limits.gifts_per_minute, 1, 600) ?? undefined,
    "rate_limits.cloud_save_per_minute":
      rangeError(cfg.rate_limits.cloud_save_per_minute, 1, 600) ?? undefined,
    "rate_limits.invites_per_minute":
      rangeError(cfg.rate_limits.invites_per_minute, 1, 600) ?? undefined,
    "profile.meta_schema": metaSchemaError(cfg.profile.meta_schema),
    "cloud_save.max_bytes": cloudSaveMaxBytesError(cfg.cloud_save.max_bytes),
  };
}

// The form's unit is MiB and the blob's is bytes; these two are the whole of
// the conversion, and the bounds are the server's (internal/cloudsave/config.go
// clamps to the same 1–64 MiB, so the box refuses exactly what the server
// would silently round).
const MIB = 1048576;
const CLOUD_SAVE_MIN_MIB = 1;
const CLOUD_SAVE_MAX_MIB = 64;

function bytesToMiB(bytes: number): number {
  return Number.isFinite(bytes) ? bytes / MIB : NaN;
}

function mibToBytes(mib: number): number {
  return Number.isFinite(mib) ? Math.round(mib * MIB) : NaN;
}

/**
 * The errors the field can show, in the unit the operator typed in. A blank
 * box is "Required" like every other number on the page; a fraction — typed
 * here, or bytes in Raw JSON that are not a whole number of MiB — is refused
 * rather than rounded, so what the box shows is what the blob holds.
 */
function cloudSaveMaxBytesError(bytes: number): string | undefined {
  if (!Number.isFinite(bytes)) return "Required";
  if (bytes < CLOUD_SAVE_MIN_MIB * MIB || bytes > CLOUD_SAVE_MAX_MIB * MIB) {
    return `Must be between ${CLOUD_SAVE_MIN_MIB} and ${CLOUD_SAVE_MAX_MIB} MiB.`;
  }
  if (bytes % MIB !== 0) return "Must be a whole number of MiB.";
  return undefined;
}

/**
 * One of the Help requests section's two scalar durations — Unclaimed lifetime
 * and Default TTL.
 *
 * A minute to thirty days, and `0` besides. The floor keeps a typo from burning
 * what a player has not opened the game to collect yet; the ceiling is the point
 * past which the word "expires" stops meaning anything. The SAME span a type's
 * own `ttl` obeys (`helpTypes.ts`), so the section's three durations cannot
 * disagree about what a sane length of time is.
 *
 * `0` is exempt from the range because it is not a duration at all: it is the ∞
 * mode, the "never" all three keys are read with. Refusing it here would not
 * merely hide the option — this validation gates Save for the WHOLE page, so a
 * project that switched a field to ∞ could not change any other setting either.
 */
function ttlFieldError(seconds: number): string | undefined {
  if (seconds === 0) return undefined;
  return rangeError(seconds, TTL_MIN, TTL_MAX) ?? undefined;
}

/**
 * The one thing that can be WRONG about the clock skew, as opposed to merely
 * overruled: an empty box, which would be written back as `null`.
 *
 * Nothing else here blocks Save, and that is deliberate — this validation gates
 * the whole page. A value the server clamps and a value it cannot read are both
 * shown as warnings beside the field, because the operator who came to fix an
 * unrelated section must not be held hostage by a number the platform already
 * has an answer for. Zero and negative are not even that: they are the ordinary
 * "not configured" every numeric setting in this codebase reads as the default.
 */
function signingSkewError(value: unknown): string | undefined {
  if (typeof value !== "number") return undefined;
  return Number.isNaN(value) ? "Required" : undefined;
}

/**
 * Block Save on a malformed meta schema. MetaSchemaEditor already flags the
 * offending row inline; this lifts the same two rules to the save bar, because
 * a duplicate key silently loses one of the entries when the backend folds the
 * array into a key→field map.
 */
function metaSchemaError(
  schema: ProfileConfig["meta_schema"],
): string | undefined {
  const seen = new Set<string>();
  for (const f of schema) {
    if (!f.key) return "Every meta field needs a key.";
    if (!META_KEY_REGEX.test(f.key)) {
      return `Invalid meta key “${f.key}” — use lowercase letters, digits, underscore.`;
    }
    if (seen.has(f.key)) return `Duplicate meta key “${f.key}”.`;
    seen.add(f.key);
  }
  return undefined;
}
